// HRDashboard.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSignOutAlt,
  FaBell,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaUserCircle,
} from "react-icons/fa";
import {
  Users,
  CalendarDays,
  ClipboardList,
  FileText,
  BarChart2,
  PlusCircle,
  Eye,
} from "lucide-react";
import "./HRDashboard.css";

// ✅ Import HR-specific modules
import ViewEmployees from "../components/AllEmployees";
import LeaveList from "../components/LeaveList";
import EmployeeForm from "../components/AddEmployee";
import EditProfile from "../components/EditProfile";
import AssignLeaves from "../components/AssignLeaves";
import ViewLeaveCreditLogs from "../components/ViewLeaveCreditLogs";
import AdminDocuments from "../components/AdminDocuments";
import HRLetterGenerator from "./HRLetterGenerator";
import API_BASE_URL from "../config";

// Placeholder
const EmptyPage = () => <h2>Coming Soon...</h2>;

const HRDashboard = () => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState("Overview");
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [username, setUsername] = useState("HR Manager");

  // ✅ Role management states
  const [roles, setRoles] = useState([]);
  const [showRolePopup, setShowRolePopup] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    fetch(`${API_BASE_URL}/api/dashboard`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch HR info");
        return res.json();
      })
      .then((data) => {
        if (data.username) setUsername(data.username);
        if (data.roles) setRoles(data.roles); // ✅ Save roles
      })
      .catch((err) => {
        console.error(err);
        sessionStorage.removeItem("token");
        navigate("/");
      });
  }, [navigate]);

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    setProfileOpen(false);
  };

  const handleAction = (type) => {
    if (type === "Logout") {
      sessionStorage.removeItem("token");
      navigate("/");
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case "Overview":
        return (
          <section className="hr-stats">
            <div className="cards blue">
              <Users size={28} />
              <h4>Total Employees</h4>
              <p>25</p>
              <button onClick={() => handleMenuClick("Employees")}>
                <Eye size={16} /> View
              </button>
            </div>

            <div className="cards green">
              <ClipboardList size={28} />
              <h4>Leave Requests</h4>
              <p>6 Pending</p>
              <button onClick={() => handleMenuClick("LeaveRequests")}>
                <Eye size={16} /> Manage
              </button>
            </div>

            <div className="cards yellow">
              <FileText size={28} />
              <h4>Reports</h4>
              <p>Monthly HR Reports</p>
              <button onClick={() => handleMenuClick("Reports")}>
                <Eye size={16} /> View
              </button>
            </div>
          </section>
        );
      case "Employees":
        return <ViewEmployees />;
      case "AddEmployee":
        return <EmployeeForm />;
      case "LeaveRequests":
        return <LeaveList />;
      case "EditProfile":
        return <EditProfile />;
      case "Assign Leaves":
        return <AssignLeaves />;
      case "ViewCreditLeaves":
        return <ViewLeaveCreditLogs />;
      case "UserDocuments":
        return <AdminDocuments />;
      case "HRLetterGenerator":
        return <HRLetterGenerator />;
      default:
        return <EmptyPage />;
    }
  };

  return (
    <div className="hr-dashboard">
      {/* Sidebar */}
      <aside className="hr-sidebar">
        <h2 className="logo">HR Portal</h2>
        <ul>
          <li
            className={activeMenu === "Overview" ? "active" : ""}
            onClick={() => handleMenuClick("Overview")}
          >
            <BarChart2 size={18} /> Overview
          </li>

          {/* Employee Dropdown */}
          <li
            className="dropdown-header"
            onClick={() => setEmployeeOpen(!employeeOpen)}
          >
            <Users size={18} /> Employees{" "}
            {employeeOpen ? <FaChevronUp /> : <FaChevronDown />}
          </li>
          {employeeOpen && (
            <ul className="dropdown">
              <li
                className={activeMenu === "Employees" ? "active" : ""}
                onClick={() => handleMenuClick("Employees")}
              >
                <Eye size={16} /> View Employees
              </li>
              <li
                className={activeMenu === "AddEmployee" ? "active" : ""}
                onClick={() => handleMenuClick("AddEmployee")}
              >
                <PlusCircle size={16} /> Add Employee
              </li>
            </ul>
          )}

          {/* Leave Dropdown */}
          <li
            className="dropdown-header"
            onClick={() => setLeaveOpen(!leaveOpen)}
          >
            <CalendarDays size={18} /> Leave Management{" "}
            {leaveOpen ? <FaChevronUp /> : <FaChevronDown />}
          </li>
          {leaveOpen && (
            <ul className="dropdown">
              <li
                className={activeMenu === "LeaveRequests" ? "active" : ""}
                onClick={() => handleMenuClick("LeaveRequests")}
              >
                <ClipboardList size={16} /> Leave Requests
              </li>
              <li
                className={activeMenu === "Assign Leaves" ? "active" : ""}
                onClick={() => handleMenuClick("Assign Leaves")}
              >
                <ClipboardList size={16} /> Assign Leaves
              </li>
              <li
                className={activeMenu === "ViewCreditLeaves" ? "active" : ""}
                onClick={() => handleMenuClick("ViewCreditLeaves")}
              >
                <ClipboardList size={16} /> Credited Leaves
              </li>
            </ul>
          )}
        </ul>
            <li
                        className={activeMenu === "UserDocuments" ? "active" : ""}
                        onClick={() => handleMenuClick("UserDocuments")}
                      >
                        <FileText size={18} /> User Documents
                      </li>

          <li
                        className={activeMenu === "HRLetterGenerator" ? "active" : ""}
                        onClick={() => handleMenuClick("HRLetterGenerator")}
                      >
                        <FileText size={18} /> Letter Management
                      </li>
        <button
          className="logout-btn"
          onClick={() => handleAction("Logout")}
        >
          <FaSignOutAlt /> Logout
        </button>
      </aside>

      {/* Main Section */}
      <main className="hr-main">
        <header className="hr-navbar">
          <h3>{activeMenu}</h3>
          <div className="nav-actions">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input type="text" placeholder="Search..." />
            </div>
            <FaBell className="icon-btn" />

            {/* Profile with Role Change */}
            <div
              className="profile"
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <img
                src="https://i.pravatar.cc/40"
                alt="HR"
                className="profile-pic"
              />
              <span className="welcome">👋 Hi, {username}</span>
              {profileOpen && (
                <div className="profile-dropdown">
                  <button onClick={() => handleMenuClick("EditProfile")}>
                    <FaUserCircle /> Profile
                  </button>
                  {/* ✅ Change Role if multiple roles */}
                  {roles.length > 1 && (
                    <button onClick={() => setShowRolePopup(true)}>
                      🔄 Change Role
                    </button>
                  )}
                  <button onClick={() => handleAction("Logout")}>
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="hr-content">{renderContent()}</div>

        {/* 🔄 Role Change Popup */}
        {showRolePopup && (
          <div className="popup-overlay">
            <div className="popup-forms">
              <h3>🔄 Select Role</h3>

              <table className="role-table">
                <tbody>
                  {roles.map((role, idx) => {
                    let path = "/";
                    if (role === "ROLE_MANAGER") path = "/managerdashboard";
                    if (role === "ROLE_ADMIN") path = "/admindashboard";
                    if (role === "ROLE_USER") path = "/empdashboard";
                    if (role === "ROLE_HR") path = "/hrdashboard"; // ✅ HR Role

                    return (
                      <tr key={idx}>
                        <td>
                          <a
                            href={path}
                            className="role-link"
                            onClick={() => setShowRolePopup(false)}
                          >
                            {role.replace("ROLE_", "")}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="close-btns-wrapper">
                <button
                  className="close-btns"
                  onClick={() => setShowRolePopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HRDashboard;
